import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  TextField,
  Button,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
} from '@mui/material';
import {
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { getWeekEntriesGrouped, getEntriesByDateRange } from './api';
import { formatDate } from './utils';

const DRAWER_WIDTH = 260;
const HOURLY_RATE = 90;

const ReportsStandalone = ({ user, onLogout, onNavigate }) => {
  const [currentUser] = useState(user.username);
  const [adminMode, setAdminMode] = useState(false); // Toggle for admin view
  
  // Date filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Data
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Totals
  const [totalHours, setTotalHours] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [billableHours, setBillableHours] = useState(0);
  const [nonBillableHours, setNonBillableHours] = useState(0);

  // Initialize with current month first half
  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    
    setStartDate(`${year}-${month}-01`);
    setEndDate(`${year}-${month}-15`);
  }, []);

  const setDateRange = (rangeType) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    
    let start, end;
    
    switch(rangeType) {
      case 'first-half':
        start = new Date(year, month, 1);
        end = new Date(year, month, 15);
        break;
      case 'second-half':
        start = new Date(year, month, 16);
        end = new Date(year, month + 1, 0);
        break;
      case 'current-month':
        start = new Date(year, month, 1);
        end = new Date(year, month + 1, 0);
        break;
      case 'last-month':
        start = new Date(year, month - 1, 1);
        end = new Date(year, month, 0);
        break;
      default:
        return;
    }
    
    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
  };

  const loadReport = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Fetch entries based on mode
      const data = adminMode 
        ? await getEntriesByDateRange(currentUser, startDate, endDate)
        : await getWeekEntriesGrouped(currentUser, startDate);
      console.log('=== DEBUG START ===');
      console.log('1. Fetched data:', data);
      console.log('2. Number of items:', data.items?.length || 0);
      console.log('3. Current user:', currentUser);
      console.log('3a. Admin mode:', adminMode);
      console.log('4. Start date filter:', startDate);
      console.log('5. End date filter:', endDate);
      
      if (data.items && data.items.length > 0) {
        console.log('6. First entry full object:', data.items[0]);
        console.log('7. First entry date:', data.items[0].start_date);
        console.log('8. First entry user:', data.items[0].user_);
        console.log('9. Date type:', typeof data.items[0].start_date);
        
        // Test date parsing on first entry
        const testDate = data.items[0].start_date;
        if (testDate.includes('T')) {
          const parsed = new Date(testDate);
          console.log('10. Parsed as ISO:', parsed);
          console.log('11. Parsed date string:', parsed.toISOString());
        }
      }
      console.log('=== DEBUG END ===');
      
      if (!data.items || data.items.length === 0) {
        setError('No entries found for the selected date range');
        setEntries([]);
        setSummary([]);
        setTotalHours(0);
        setTotalRevenue(0);
        setBillableHours(0);
        setNonBillableHours(0);
        setLoading(false);
        return;
      }
      
      // Filter entries to exact date range on client side
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      
      const filteredEntries = data.items.filter(entry => {
        if (!entry.start_date) {
          return false;
        }
        
        let entryDate;
        
        // Handle different date formats from APEX
        if (typeof entry.start_date === 'string') {
          if (entry.start_date.includes('/')) {
            // Format: MM/DD/YYYY
            const [month, day, year] = entry.start_date.split('/');
            entryDate = new Date(year, month - 1, day);
          } else if (entry.start_date.includes('T')) {
            // Format: ISO timestamp (2025-11-03T00:00:00Z)
            entryDate = new Date(entry.start_date);
          } else if (entry.start_date.includes('-')) {
            // Format: YYYY-MM-DD
            entryDate = new Date(entry.start_date + 'T00:00:00');
          } else {
            return false;
          }
        } else {
          // If it's already a Date object
          entryDate = new Date(entry.start_date);
        }
        
        // Make sure we got a valid date
        if (isNaN(entryDate.getTime())) {
          console.log('Invalid date:', entry.start_date);
          return false;
        }
        
        return entryDate >= start && entryDate <= end;
      });
      
      console.log('=== FILTERING DEBUG ===');
      console.log('Total entries before filter:', data.items.length);
      console.log('Filtered entries count:', filteredEntries.length);
      console.log('Date range:', startDate, 'to', endDate);
      console.log('Start date object:', start);
      console.log('End date object:', end);
      
      // Show why entries were filtered out
      if (filteredEntries.length === 0 && data.items.length > 0) {
        console.log('Sample entries that were filtered OUT:');
        data.items.slice(0, 3).forEach((entry, idx) => {
          console.log(`Entry ${idx + 1}:`, {
            date: entry.start_date,
            user: entry.user_,
            client: entry.client,
            description: entry.description?.substring(0, 30)
          });
          
          // Try to parse and show comparison
          if (entry.start_date && entry.start_date.includes('T')) {
            const entryDate = new Date(entry.start_date);
            console.log(`  Parsed date: ${entryDate.toISOString()}`);
            console.log(`  Is after start? ${entryDate >= start}`);
            console.log(`  Is before end? ${entryDate <= end}`);
          }
        });
      }
      console.log('=== END FILTERING DEBUG ===');
      
      if (filteredEntries.length === 0) {
        setError('No entries found for the selected date range');
        setEntries([]);
        setSummary([]);
        setTotalHours(0);
        setTotalRevenue(0);
        setBillableHours(0);
        setNonBillableHours(0);
        setLoading(false);
        return;
      }
      
      setEntries(filteredEntries);
      
      // Calculate summary by company
      const companySummary = {};
      let totalHrs = 0;
      let billableHrs = 0;
      let nonBillableHrs = 0;
      
      filteredEntries.forEach(entry => {
        const company = entry.client || 'Unknown';
        const hours = parseFloat(entry.duration) || 0;
        const isBillable = entry.billable === 'Y' || entry.billable === 'YES';
        
        if (!companySummary[company]) {
          companySummary[company] = {
            company: company,
            totalHours: 0,
            billableHours: 0,
            nonBillableHours: 0,
            revenue: 0,
          };
        }
        
        companySummary[company].totalHours += hours;
        
        if (isBillable) {
          companySummary[company].billableHours += hours;
          companySummary[company].revenue += hours * HOURLY_RATE;
          billableHrs += hours;
        } else {
          companySummary[company].nonBillableHours += hours;
          nonBillableHrs += hours;
        }
        
        totalHrs += hours;
      });
      
      const summaryArray = Object.values(companySummary).sort(
        (a, b) => b.totalHours - a.totalHours
      );
      
      setSummary(summaryArray);
      setTotalHours(totalHrs);
      setTotalRevenue(billableHrs * HOURLY_RATE);
      setBillableHours(billableHrs);
      setNonBillableHours(nonBillableHrs);
      
    } catch (err) {
      console.error('Error loading report:', err);
      setError('Failed to load report: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (summary.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['Company', 'Total Hours', 'Billable Hours', 'Non-Billable Hours', 'Revenue'];
    const rows = summary.map(item => [
      item.company,
      item.totalHours.toFixed(2),
      item.billableHours.toFixed(2),
      item.nonBillableHours.toFixed(2),
      `$${item.revenue.toFixed(2)}`
    ]);
    
    rows.push([
      'TOTAL',
      totalHours.toFixed(2),
      billableHours.toFixed(2),
      nonBillableHours.toFixed(2),
      `$${totalRevenue.toFixed(2)}`
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time-report-${startDate}-to-${endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      {/* Left Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            bgcolor: '#4a5d8f',
            color: 'white',
            borderRight: 'none',
          },
        }}
      >

        {/* Logo/Brand */}
        <Box sx={{ p: 3, pb: 2, textAlign: 'center' }}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <img
              src="/logo.png"
              alt="SBAS Logo"
              style={{
                width: '180px',
                maxWidth: '100%',
                height: 'auto',
              }}
            />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
            Time Tracking
          </Typography>

          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            by SBAS
          </Typography>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mx: 2 }} />

        {/* Navigation */}
        <List sx={{ px: 2, py: 3 }}>
          <ListItem
            button
            onClick={() => onNavigate && onNavigate('timesheet')}
            sx={{
              borderRadius: 2,
              mb: 1,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}>
              <ScheduleIcon />
            </ListItemIcon>
            <ListItemText
              primary="My Timesheet"
              primaryTypographyProps={{
                fontWeight: 400,
                fontSize: '0.95rem',
                color: 'rgba(255,255,255,0.85)',
              }}
            />
          </ListItem>

          <ListItem
            button
            selected
            sx={{
              borderRadius: 2,
              mb: 1,
              '&.Mui-selected': {
                bgcolor: 'rgba(255,255,255,0.15)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
              <AssessmentIcon />
            </ListItemIcon>
            <ListItemText
              primary="Reports"
              primaryTypographyProps={{
                fontWeight: 500,
                fontSize: '0.95rem',
              }}
            />
          </ListItem>

          {/* Employees menu - Admin only */}
          {user?.isAdmin && (
            <ListItem
              button
              onClick={() => onNavigate && onNavigate('employees')}
              sx={{
                borderRadius: 2,
                mb: 1,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}>
                <PeopleIcon />
              </ListItemIcon>
              <ListItemText
                primary="Employees"
                primaryTypographyProps={{
                  fontWeight: 400,
                  fontSize: '0.95rem',
                  color: 'rgba(255,255,255,0.85)',
                }}
              />
            </ListItem>
          )}
        </List>

        {/* User Profile at Bottom */}
        <Box sx={{ mt: 'auto', p: 2 }}>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ width: 40, height: 40, bgcolor: '#5d7ab8' }}>
              {user?.name?.charAt(0) || 'U'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', fontSize: '0.9rem' }}>
                {user?.name || 'User'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {user?.role || 'User'}
              </Typography>
            </Box>
            <IconButton
              onClick={onLogout}
              size="small"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { color: 'white' },
              }}
            >
              🚪
            </IconButton>
          </Stack>
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1 }}>
          Time Reports
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Generate summary reports by date range
        </Typography>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters Card */}
      <Card sx={{ 
        mb: 4,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        borderRadius: 3,
        border: '1px solid #e8ecf1'
      }}>
        <Box sx={{ p: 3, bgcolor: '#fafbfc', borderBottom: '1px solid #e8ecf1' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
            Date Range Filters
          </Typography>
        </Box>
        
        <Box sx={{ p: 3 }}>
          {/* Admin Mode Toggle - Admin only */}
          {user?.isAdmin && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 2, border: '1px solid #ffb74d' }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>
                  Admin Mode:
                </Typography>
                <Button 
                  variant={!adminMode ? "contained" : "outlined"}
                  size="small"
                  onClick={() => setAdminMode(false)}
                  sx={{ textTransform: 'none' }}
                >
                  My Entries Only
                </Button>
                <Button 
                  variant={adminMode ? "contained" : "outlined"}
                  size="small"
                  onClick={() => setAdminMode(true)}
                  sx={{ 
                    textTransform: 'none',
                    bgcolor: adminMode ? '#ff9800' : 'transparent',
                    color: adminMode ? 'white' : 'inherit',
                    '&:hover': { bgcolor: adminMode ? '#f57c00' : 'rgba(255, 152, 0, 0.08)' }
                  }}
                >
                  All Users (Admin)
                </Button>
                {adminMode && (
                  <Typography variant="caption" sx={{ color: '#e65100', fontStyle: 'italic' }}>
                    👥 Viewing all users' data
                  </Typography>
              )}
            </Stack>
          </Box>
          )}

          {/* Quick filters */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom sx={{ fontWeight: 500, mb: 1.5 }}>
              Quick Select
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => setDateRange('first-half')}
                sx={{ textTransform: 'none' }}
              >
                1st - 15th (Current Month)
              </Button>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => setDateRange('second-half')}
                sx={{ textTransform: 'none' }}
              >
                16th - End (Current Month)
              </Button>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => setDateRange('current-month')}
                sx={{ textTransform: 'none' }}
              >
                Full Current Month
              </Button>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => setDateRange('last-month')}
                sx={{ textTransform: 'none' }}
              >
                Last Month
              </Button>
            </Stack>
          </Box>

          {/* Custom date range */}
          <Stack direction="row" spacing={3} alignItems="flex-end" flexWrap="wrap">
            <Box sx={{ minWidth: 200 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom sx={{ fontWeight: 500 }}>
                Start Date
              </Typography>
              <TextField
                type="date"
                fullWidth
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                sx={{ bgcolor: 'white' }}
              />
            </Box>

            <Box sx={{ minWidth: 200 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom sx={{ fontWeight: 500 }}>
                End Date
              </Typography>
              <TextField
                type="date"
                fullWidth
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                sx={{ bgcolor: 'white' }}
              />
            </Box>

            <Button
              variant="contained"
              size="large"
              onClick={loadReport}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, px: 4 }}
            >
              Generate Report
            </Button>
          </Stack>
        </Box>
      </Card>

      {/* Summary Statistics */}
      {summary.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <Card sx={{ flex: 1, minWidth: 200 }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Total Hours
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#4a5d8f', mt: 1 }}>
                  {totalHours.toFixed(2)}
                </Typography>
              </Box>
            </Card>

            <Card sx={{ flex: 1, minWidth: 200 }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Billable Hours
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50', mt: 1 }}>
                  {billableHours.toFixed(2)}
                </Typography>
              </Box>
            </Card>

            <Card sx={{ flex: 1, minWidth: 200 }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Non-Billable Hours
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff9800', mt: 1 }}>
                  {nonBillableHours.toFixed(2)}
                </Typography>
              </Box>
            </Card>

            <Card sx={{ flex: 1, minWidth: 200, bgcolor: '#4a5d8f' }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="caption" sx={{ fontWeight: 500, color: 'rgba(255,255,255,0.8)' }}>
                  Total Revenue
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mt: 1 }}>
                  {formatCurrency(totalRevenue)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  @ ${HOURLY_RATE}/hr
                </Typography>
              </Box>
            </Card>
          </Stack>
        </Box>
      )}

      {/* Summary Table */}
      {summary.length > 0 && (
        <Card sx={{ 
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderRadius: 3,
          border: '1px solid #e8ecf1',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            p: 3, 
            bgcolor: '#fafbfc',
            borderBottom: '1px solid #e8ecf1',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                Summary by Company
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {startDate} to {endDate} • {entries.length} entries
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={exportToCSV}
              sx={{ textTransform: 'none' }}
            >
              Export CSV
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#2c3e50' }}>Company</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#2c3e50' }}>Total Hours</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#2c3e50' }}>Billable Hours</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#2c3e50' }}>Non-Billable</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#2c3e50' }}>Revenue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.map((row, index) => (
                  <TableRow 
                    key={index}
                    sx={{ '&:hover': { bgcolor: '#fafbfc' } }}
                  >
                    <TableCell sx={{ fontWeight: 500, color: '#2c3e50' }}>
                      {row.company}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#4a5d8f', fontWeight: 600 }}>
                      {row.totalHours.toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#4caf50' }}>
                      {row.billableHours.toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#757575' }}>
                      {row.nonBillableHours.toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#4a5d8f' }}>
                      {formatCurrency(row.revenue)}
                    </TableCell>
                  </TableRow>
                ))}
                
                {/* Totals Row */}
                <TableRow sx={{ bgcolor: '#f8f9fb', borderTop: '2px solid #e0e0e0' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#2c3e50' }}>
                    TOTAL
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#4a5d8f' }}>
                    {totalHours.toFixed(2)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#4caf50' }}>
                    {billableHours.toFixed(2)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#757575' }}>
                    {nonBillableHours.toFixed(2)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#4a5d8f', fontSize: '1.1rem' }}>
                    {formatCurrency(totalRevenue)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Empty state */}
      {!loading && summary.length === 0 && !error && (
        <Card sx={{ 
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderRadius: 3,
          border: '1px solid #e8ecf1',
          p: 6,
          textAlign: 'center'
        }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Report Generated
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select a date range and click "Generate Report" to view summary
          </Typography>
        </Card>
      )}
      </Box>
    </Box>
  );
};

export default ReportsStandalone;